(ns ductile.ui.agent-stream
  (:require [clojure.string :as str]))

;; Streaming (SSE) client for the agent.
;;
;; Uses fetch + ReadableStream because we want POST with body params (EventSource
;; is GET-only).
;;
;; SSE frame format expected from backend:
;;   event: token\n
;;   data: {"text":"..."}\n\n
;;
;; Supported event types:
;; - meta  {request-id ...}
;; - token {text "..."}
;; - done  {}
;; - error {message "..."}
;;
;; NOTE: no global state here; caller passes in callbacks.


(defn- parse-sse-frames
  "Parse an SSE text chunk into a vector of event maps.

  Returns [events remainder] where remainder is any partial frame which must be
  prepended to the next chunk."
  [s]
  (let [frames (str/split s #"\n\n")
        trailing? (str/ends-with? s "\n\n")]
    (if (and (<= (count frames) 1) (not trailing?))
      [[] s]
      (let [complete (if trailing?
                       frames
                       (butlast frames))
            remainder (if trailing?
                        ""
                        (last frames))
            parse-frame
            (fn [frame]
              (let [lines (->> (str/split-lines frame)
                               (map str/trim)
                               (remove str/blank?))
                    event (some->> lines (some #(when (str/starts-with? % "event:") (subs % 6))) str/trim)
                    data-line (some->> lines (some #(when (str/starts-with? % "data:") (subs % 5))) str/trim)
                    data (when (and data-line (not (str/blank? data-line)))
                           (try
                             (js->clj (.parse js/JSON data-line) :keywordize-keys true)
                             (catch :default _
                               {:raw data-line})))]
                (when (or event data)
                  {:event event
                   :data data
                   :raw frame})))]
        [(->> complete (map parse-frame) (remove nil?) vec)
         remainder]))))

(defn stream!
  "Start SSE stream via fetch.

  opts:
  - :url (string)
  - :body (clj map) -> JSON
  - :on-open (fn [])
  - :on-event (fn [{:keys [event data raw]}])
  - :on-error (fn [err])
  - :on-done (fn [])

  Returns {:abort! (fn [])} handle."
  [{:keys [url body on-open on-event on-error on-done]}]
  (let [abort-ctrl (js/AbortController.)
        decoder (js/TextDecoder. "utf-8")
        !remainder (atom "")
        read-loop!
        (fn read-loop! [reader]
          (-> (.read reader)
              (.then (fn [res]
                       (let [done (.-done res)
                             value (.-value res)]
                         (if done
                           (when on-done (on-done))
                           (let [chunk (.decode decoder value)
                                 combined (str @!remainder chunk)
                                 [events remainder] (parse-sse-frames combined)]
                             (reset! !remainder remainder)
                             (doseq [ev events]
                               (when on-event (on-event ev)))
                             (read-loop! reader))))))
              (.catch (fn [err]
                        (when (and on-error
                                   ;; AbortError is expected for cancellation
                                   (not= "AbortError" (.-name err)))
                          (on-error err))))))]
    (-> (js/fetch url
                  (clj->js {:method "POST"
                            :headers {"Content-Type" "application/json"
                                      "Accept" "text/event-stream"}
                            :body (js/JSON.stringify (clj->js body))
                            :credentials "same-origin"
                            :signal (.-signal abort-ctrl)}))
        (.then (fn [resp]
                 (if (.-ok resp)
                   (do
                     (when on-open (on-open))
                     (read-loop! (.getReader (.-body resp))))
                   (-> (.text resp)
                       (.then (fn [txt]
                                (when on-error
                                  (on-error (js/Error. (str "HTTP " (.-status resp) ": " txt))))))))))
        (.catch (fn [err]
                  (when (and on-error (not= "AbortError" (.-name err)))
                    (on-error err))))
        (.finally (fn []
                    ;; ensure caller can finish lifecycle even if server closes before a final SSE frame
                    (when on-done (on-done)))))

    {:abort! (fn [] (.abort abort-ctrl))}))
